import { findPath, isWalkable, metricDistance, type NavigationMap, type NavPosition } from '../navigation/index.ts';
import { type AutonomousDecision, type CombatAction, type CombatState } from '../combat/index.ts';
import type { CompanionContract, GoalKind } from '../companions/contracts.ts';
import { evaluateCompanion } from './goals.ts';

export { assessDanger, DANGER_RANGE, type DangerReport } from './danger.ts';
export { evaluateCompanion, scoreGoals, type GoalAssessment, type ScoredGoal, type VetoedGoal } from './goals.ts';

/**
 * Autonomous companion decisions on D01.1's tactical interfaces. The adapter
 * returns an `AutonomousDecision`: one immediate action for the deciding
 * companion only. It never queues PC intents, never addresses another actor,
 * and never attacks anyone but a live enemy — there is no command path that
 * targets a companion, only each companion's own judgement from its contract.
 */
export type ContractRegistry = Readonly<Record<string, CompanionContract>>;

const compareText = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
const positionKey = (position: NavPosition): string => `${position.layer}:${position.x}:${position.y}`;

function nearestEnemy(state: Readonly<CombatState>, actorId: string): string | null {
  const self = state.actors[actorId]!;
  let best: string | null = null;
  let bestDistance = Number.MAX_SAFE_INTEGER;
  for (const id of Object.keys(state.actors).sort()) {
    if (id === actorId) continue;
    const other = state.actors[id]!;
    if (other.role !== 'enemy' || other.status === 'dead') continue;
    const distance = metricDistance(self.position, other.position);
    if (distance === null) continue;
    if (distance < bestDistance || (distance === bestDistance && best !== null && compareText(id, best) < 0)) {
      best = id;
      bestDistance = distance;
    }
  }
  return best;
}

function moveToward(map: NavigationMap, state: Readonly<CombatState>, actorId: string, target: NavPosition): CombatAction | null {
  const self = state.actors[actorId]!;
  if (self.position.x === target.x && self.position.y === target.y && self.position.layer === target.layer) return null;
  const route = findPath(map, self.position, target);
  if (route === null || route.length === 0) return null;
  return { type: 'move', destination: { ...target } };
}

/** Withdraw to the neighbouring walkable cell farthest from live enemies. Ties break on position key. */
function withdrawStep(map: NavigationMap, state: Readonly<CombatState>, actorId: string): CombatAction | null {
  const self = state.actors[actorId]!;
  const foes = Object.keys(state.actors)
    .sort()
    .filter(id => id !== actorId)
    .map(id => state.actors[id]!)
    .filter(other => other.role === 'enemy' && other.status !== 'dead');
  const candidates = [self.position];
  const steps = [
    { x: self.position.x - map.cellSize, y: self.position.y, layer: self.position.layer },
    { x: self.position.x, y: self.position.y - map.cellSize, layer: self.position.layer },
    { x: self.position.x, y: self.position.y + map.cellSize, layer: self.position.layer },
    { x: self.position.x + map.cellSize, y: self.position.y, layer: self.position.layer },
  ];
  const occupied = new Set(
    Object.values(state.actors)
      .filter(other => other.id !== actorId && other.status !== 'dead')
      .map(other => positionKey(other.position)),
  );
  for (const step of steps) {
    if (isWalkable(map, step) && !occupied.has(positionKey(step))) candidates.push(step);
  }
  const safety = (position: NavPosition): number => {
    let worst = Number.MAX_SAFE_INTEGER;
    for (const foe of foes) {
      const distance = metricDistance(position, foe.position);
      if (distance === null) continue;
      if (distance < worst) worst = distance;
    }
    return worst;
  };
  const here = safety(self.position);
  const ordered = [...candidates].sort((a, b) => compareText(positionKey(a), positionKey(b)));
  let best = self.position;
  let bestSafety = here;
  for (const candidate of ordered) {
    const value = safety(candidate);
    if (value > bestSafety) {
      best = candidate;
      bestSafety = value;
    }
  }
  if (bestSafety <= here) return null;
  return { type: 'move', destination: { ...best } };
}

function actOnGoal(map: NavigationMap, state: Readonly<CombatState>, actorId: string, goal: GoalKind): CombatAction | null {
  const self = state.actors[actorId]!;
  // A hired hold keeps its ground against a foe; with no foe about, it keeps to your side like a guard.
  if (goal === 'hold' && nearestEnemy(state, actorId) !== null) return null;
  if (goal === 'withdraw') return withdrawStep(map, state, actorId);
  if (goal === 'guard' || goal === 'hold') {
    const pc = state.actors[state.pcId];
    if (!pc || pc.status === 'dead') return null;
    const distance = metricDistance(self.position, pc.position);
    if (distance === null || distance <= Math.max(map.cellSize, self.movePerTick)) return null;
    const route = findPath(map, self.position, pc.position);
    // Guard beside the PC. Moving to their exact cell makes the two bodies overlap.
    const beside = route?.at(-2)?.position;
    return beside ? moveToward(map, state, actorId, beside) : null;
  }
  const foeId = nearestEnemy(state, actorId);
  if (foeId === null) return null;
  const foe = state.actors[foeId]!;
  const distance = metricDistance(self.position, foe.position);
  if (distance !== null && distance <= self.attackRange) return { type: 'attack', targetId: foeId };
  return moveToward(map, state, actorId, foe.position);
}

export function createCompanionDecision(map: NavigationMap, registry: ContractRegistry): AutonomousDecision {
  const contracts = structuredClone(registry);
  const frozenMap = structuredClone(map);
  return (state, actorId) => {
    const contract = contracts[actorId];
    const actor = state.actors[actorId];
    if (!contract || !actor || actor.role !== 'companion' || actor.status === 'dead' || actor.id !== contract.companionId) return null;
    const assessment = evaluateCompanion(contract, state);
    if (assessment.chosen === null) return null;
    return actOnGoal(frozenMap, state, actorId, assessment.chosen);
  };
}
